import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http'
import { AuthService } from 'app/security/auth.service';
import { Observable, Subject } from 'rxjs';
import { PolicyParamDTO } from 'app/header-footer-template/model/PolicyParamDTO';
import { saveAs } from 'file-saver';
import { ToastrService } from 'ngx-toastr';
import { DomainService } from './domain-service';
import { ResetService } from './reset-service';
import { CommonsText } from 'app/commons/CommonsText';

/**
 * Relación del cliente, como TOMADOR, con las pólizas de la entidad activa.
 */
export interface TakerSummary {
    /** Tiene al menos una póliza en calidad de tomador. */
    isTaker: boolean;
    /** Ids de producto de esas pólizas (facet 'product' que devuelve /policies/search). */
    productIds: string[];
}

@Injectable({
    providedIn: 'root'
})

/**
 * Servicio de Polizas
 * @author Iecisa
 */
export class PolicyService {

    // Producto "Asistencia Jurídica Familiar".
    private static readonly LEGAL_ASSISTANCE_PRODUCT_ID = 'DJ29';
    // Productos que dan acceso a "Mis beneficios".
    private static readonly BENEFIT_PRODUCT_IDS = ['PP09', 'PP11', 'DJ13', 'DJ29'];
    // Productos que dan acceso a Legaliboo.
    private static readonly LEGALIBOO_PRODUCT_IDS = ['PP09', 'PP11'];
    // Situación "En vigor" de Innova, tal como la espera el filtro statusType de la búsqueda.
    private static readonly POLICY_STATUS_ACTIVE = 'V';

    private pageSizeChange;
    private pageOffsetChange;
    private pageFilterChange;
    oldParams: PolicyParamDTO = new PolicyParamDTO();

    // Una única búsqueda cacheada (clave documento+entidad) de la que salen todos los
    // flags de visibilidad que dependen de ser tomador. La caché se limpia en el logout.
    private takerSummaryPromise: Promise<TakerSummary> = null;
    private takerSummaryKey: string = null;

    // Asistencia Jurídica va aparte porque además exige que la póliza esté en vigor, cosa
    // que takerSummary() no distingue. Se cachea igual, por documento + entidad.
    private legalAssistancePromise: Promise<boolean> = null;
    private legalAssistanceKey: string = null;

    constructor(
        private http: HttpClient, private authService: AuthService,
        private toastr: ToastrService, private domainService: DomainService,
        private resetService: ResetService) {
        // Al hacer logout limpiamos la caché para no reutilizarla en una sesión posterior
        // (aunque vuelva a entrar el mismo usuario/entidad).
        this.resetService.observerLogout().subscribe(() => {
            this.takerSummaryPromise = null;
            this.takerSummaryKey = null;
            this.legalAssistancePromise = null;
            this.legalAssistanceKey = null;
        });
    }

    public setPageSize(value: any) {
        this.pageSizeChange.next(value);
    }

    public setPageOffset(value: any) {
        this.pageOffsetChange.next(value);
    }

    public setPageFilter(value: any) {
        this.pageFilterChange.next(value);
    }

    observerPageSizeChange(): Observable<any> {
        this.pageSizeChange = new Subject<any>();
        return this.pageSizeChange.asObservable();
    }

    observerPageOffset(): Observable<any> {
        this.pageOffsetChange = new Subject<any>();
        return this.pageOffsetChange.asObservable();
    }

    observerPageFilter(): Observable<any> {
        this.pageFilterChange = new Subject<any>();
        return this.pageFilterChange.asObservable();
    }

    getCurrent(): Promise<any> {
        const url = this.domainService.backendUrlWithContext + '/policies/current/' + this.authService.getSelectedEntity();
        return this.http.get(url).toPromise();
    }

    /**
     * Recupera las pólizas de la persona logada (/policies/me).
     */
    getMyPolicies(): Promise<any> {
        const url = this.domainService.backendUrlWithContext + '/policies/me';
        return this.http.get(url).toPromise();
    }

    /**
     * Resumen de las pólizas del cliente en las que es TOMADOR, en la entidad activa.
     *
     * Es una única llamada a /policies/search de la que cuelgan todos los flags de
     * visibilidad, en lugar de una búsqueda por flag. Basta con pedir una página de 1
     * porque el backend calcula el facet 'product' sobre el resultado filtrado completo,
     * antes de paginar (ver SearchPageablePoliciesBySpringUserCoreCmd).
     *
     * La condición de tomador se resuelve en el backend (isTaker) y no con los productsIds
     * del token, que no distinguen entre tomador y asegurado. El resultado se cachea por
     * documento + entidad, así que basta una llamada por sesión y aseguradora.
     */
    takerSummary(): Promise<TakerSummary> {
        const user = this.authService.getUser();
        const documentNumber = user ? user.documentNumber : '';
        const key = documentNumber + '|' + this.authService.getSelectedEntity();
        if (this.takerSummaryPromise !== null && this.takerSummaryKey === key) {
            return this.takerSummaryPromise;
        }
        this.takerSummaryKey = key;

        const params = new PolicyParamDTO();
        params.pageOffset = 0;
        params.pageSize = 1;
        params.isTaker = true;

        this.takerSummaryPromise = this.search(params)
            .then(success => {
                const products = (success && success.product) ? success.product : [];
                const total = (success && success.totalElements) ? success.totalElements : 0;
                return {
                    isTaker: total > 0,
                    productIds: products.map(p => p.id)
                } as TakerSummary;
            })
            .catch(() => {
                // No cachear el fallo: permite reintentar en la siguiente carga.
                this.takerSummaryPromise = null;
                this.takerSummaryKey = null;
                return { isTaker: false, productIds: [] } as TakerSummary;
            });
        return this.takerSummaryPromise;
    }

    /** Indica si el cliente tiene alguna póliza en calidad de tomador. */
    isTaker(): Promise<boolean> {
        return this.takerSummary().then(summary => summary.isTaker);
    }

    /** Indica si el cliente tiene acceso a "Mis beneficios". */
    hasBenefitProducts(): Promise<boolean> {
        return this.hasAnyProduct(PolicyService.BENEFIT_PRODUCT_IDS);
    }

    /** Indica si el cliente tiene acceso a Legaliboo. */
    hasLegalibooProducts(): Promise<boolean> {
        return this.hasAnyProduct(PolicyService.LEGALIBOO_PRODUCT_IDS);
    }

    /**
     * Indica si el cliente es tomador de alguna póliza de Asistencia Jurídica Familiar (DJ29)
     * **en vigor**. No sale del facet de takerSummary() porque ese no filtra por situación: una
     * DJ29 anulada mostraría "Mis asegurados" y "Mis consultas" con la pantalla vacía detrás.
     * Reutiliza la misma búsqueda que alimenta el combo de alta y se cachea por documento +
     * entidad, así que es una llamada por sesión y aseguradora.
     */
    hasLegalAssistanceProduct(): Promise<boolean> {
        const user = this.authService.getUser();
        const documentNumber = user ? user.documentNumber : '';
        const key = documentNumber + '|' + this.authService.getSelectedEntity();
        if (this.legalAssistancePromise !== null && this.legalAssistanceKey === key) {
            return this.legalAssistancePromise;
        }
        this.legalAssistanceKey = key;

        this.legalAssistancePromise = this.getLegalAssistancePolicies()
            .then(list => list.length > 0)
            .catch(() => {
                // No cachear el fallo: permite reintentar en la siguiente carga.
                this.legalAssistancePromise = null;
                this.legalAssistanceKey = null;
                return false;
            });
        return this.legalAssistancePromise;
    }

    private hasAnyProduct(productIds: string[]): Promise<boolean> {
        return this.takerSummary()
            .then(summary => summary.productIds.some(id => productIds.indexOf(id) > -1));
    }

    /**
     * Recupera las pólizas de Asistencia Jurídica Familiar (DJ29) **en vigor** de las que el
     * usuario es tomador, mediante /policies/search. Es la fuente del combo de alta de
     * asegurados: sobre una póliza anulada no se puede dar de alta a nadie.
     */
    getLegalAssistancePolicies(): Promise<any[]> {
        const params = new PolicyParamDTO();
        params.pageOffset = 0;
        params.pageSize = 9999;
        params.product = [{ id: PolicyService.LEGAL_ASSISTANCE_PRODUCT_ID }];
        params.statusType = [{ id: PolicyService.POLICY_STATUS_ACTIVE }];
        params.isTaker = true;
        return this.search(params)
            .then(success => (success && success.content) ? success.content : []);
    }

    getRelation(){
        this.getCurrent()
        .then( success =>{
            let local = 'ASEG'
                for (const policy of success.response) {
                    if(policy.isTaker){
                        local = 'TOMA';
                        break;
                    }
                }
                localStorage.setItem('relationType', local);
            }
        ).catch(error => {
            this.toastr.error( CommonsText.ERROR_SERVICIO_POLIZAS , CommonsText.ERROR_DE_OPERACION, {
                timeOut: 5000
            }); 
        });      
    }

    getPolicyByNumber(policyNumber: any): Promise<any> {
        const url = this.domainService.backendUrlWithContext + '/policies/policy/' + policyNumber;
        return this.http.get(url).toPromise();
    }

    getLegal(): Promise<any> {
        const url = this.domainService.backendUrlWithContext + '/policies/legal/' + this.authService.getSelectedEntity();
        return this.http.get(url).toPromise();
    }

    getMediators(): Promise<any> {
        const url = this.domainService.backendUrlWithContext + '/policies/mediators/' + this.authService.getSelectedEntity();
        return this.http.get(url).toPromise();
    }

    getMediator(mediatorId: any): Promise<any> {
        const url = this.domainService.backendUrlWithContext + '/policies/mediator/' + mediatorId;
        return this.http.get(url).toPromise();
    }

    haveParameterChanges(params: PolicyParamDTO): Boolean {
        if (
            (undefined !== this.oldParams.statusType &&
                undefined !== params.statusType &&
                this.oldParams.statusType.length !== params.statusType.length) ||

            (this.oldParams.policyCollective !== params.policyCollective) ||
            (this.oldParams.policyNumber !== params.policyNumber) ||

            (undefined !== this.oldParams.product &&
                undefined !== params.product &&
                this.oldParams.product.length !== params.product.length)) {

            this.oldParams.statusType = Object.assign([], params.statusType);
            this.oldParams.policyItems = Object.assign([], params.policyItems);
            this.oldParams.policyCollective = params.policyCollective;
            this.oldParams.policyNumber = params.policyNumber;
            this.oldParams.product = Object.assign([], params.product);
            return true;
        }
        return false;
    }

    search(params: PolicyParamDTO): Promise<any> {
        let httpParams = new HttpParams();

        if (undefined !== params.pageOffset) {
            if (!this.haveParameterChanges(params)) {
                httpParams = httpParams.append('pageOffset', params.pageOffset.toString());
            } else {
                httpParams = httpParams.append('pageOffset', '0');
            }
        }

        if (undefined !== params.pageSize) {
            httpParams = httpParams.append('pageSize', params.pageSize.toString());
        }

        if ((undefined !== params.startDate) && (null !== params.startDate) && ('' !== params.startDate)) {
            httpParams = httpParams.append('startDate', params.startDate);
        }

        if ((undefined !== params.endDate) && (null !== params.endDate) && ('' !== params.endDate)) {
            httpParams = httpParams.append('endDate', params.endDate);
        }

        if ((undefined !== params.policyNumber) && (null !== params.policyNumber) && ('' !== params.policyNumber.trim())) {
            httpParams = httpParams.append('policyNumber', params.policyNumber);
        }

        if ((undefined !== params.policyCollective) && (null !== params.policyCollective) && ('' !== params.policyCollective.trim())) {
            httpParams = httpParams.append('policyCollective', params.policyCollective);
        }

        if (undefined !== params.statusType) {
            for (let i = 0; i < params.statusType.length; i++) {
                httpParams = httpParams.append('statusType', params.statusType[i].id);
            }
        }

        if (undefined !== params.product) {
            for (let i = 0; i < params.product.length; i++) {
                httpParams = httpParams.append('product', params.product[i].id);
            }
        }

        if (undefined !== params.policyItems) {
            for (let i = 0; i < params.policyItems.length; i++) {
                httpParams = httpParams.append('policyItems', params.policyItems[i].id);
            }
        }

        if ((undefined !== params.isTaker) && (null !== params.isTaker)) {
            httpParams = httpParams.append('isTaker', params.isTaker.toString());
        }

        const url = this.domainService.backendUrlWithContext + '/policies/search/' + this.authService.getSelectedEntity();
        return this.http.get(url, { params: httpParams }).toPromise();
    }

    getContractFile(policyNumber: any) {

        this.toastr.info(CommonsText.PREPARANDO_ARCHIVO_DESCARGA, CommonsText.SERVICIO_DE_DOCUMENTOS, {
            timeOut: 12000
        });
        const url = this.domainService.backendUrlWithContext + '/policies/document/' + policyNumber;

        this.http.get(url, { observe: 'response', responseType: 'blob' as 'json' }).subscribe(
            (resp: HttpResponse<Blob>) => {
                this.saveToFileSystem(resp);
            },
            err => {
                this.toastr.error(CommonsText.FALLO_AL_DESCARGAR, CommonsText.SERVICIO_DE_DOCUMENTOS, {
                    timeOut: 5000
                });
            },
        );

    }

    private saveToFileSystem(response: any) {
        const contentType: string = response.headers.get('Content-Type');
        const contentDispositionHeader: string = response.headers.get('Content-Disposition');
        const parts: string[] = contentDispositionHeader.split(';');
        const filename = parts[1].split('=')[1].split('\"').join('');
        const blob = new Blob([response.body], { type: contentType });
        saveAs(blob, filename);

        this.toastr.success(CommonsText.ARCHIVO_DESCARGADO_CORRECTAMENTE, CommonsText.SERVICIO_DE_DOCUMENTOS, {
            timeOut: 5000
        });
    }

    getHirePolicyDataByNumber(policyNumber): Promise<any> {
        const url = this.domainService.backendUrlWithContext + '/policies/hireMoreInformation/' + policyNumber;
        return this.http.get(url).toPromise();
    }

    onHireMoreFormSubmit(params: any): Promise<any> {
        const form = new FormData();

        if ((undefined !== params.policyNumber) && (null !== params.policyNumber)) {
            form.append('policyNumber', params.policyNumber);
        }

        if ((undefined !== params.modificationDate) && (null !== params.modificationDate)) {
            form.append('modificationDate', params.modificationDate);
        }

        if ((undefined !== params.observation) && (null !== params.observation)) {
            form.append('observation', params.observation);
        }

        const url = this.domainService.backendUrlWithContext + '/policies/hireMore';
        return this.http.post<any>(url, form).toPromise();
    }

    changeIdentifier(params: any): Promise<any> {
        const form = new FormData();

        form.append('polizsec', params.policyNumber);
        form.append('oldIdentifiyer', params.identifier);
        form.append('newIdentifiyer', params.newIdentifier);
        form.append('modificationDate', params.modificationDate);

        const url = this.domainService.backendUrlWithContext + '/policies/changeIdentifier';
        return this.http.post<any>(url, form).toPromise();
    }

}
